import { motion } from "framer-motion";
import { Star, MapPin, ExternalLink, Quote } from "lucide-react";

const REVIEWS = [
  { initial: "P", name: "Priya D.", when: "2 months ago", text: "Best academy for GRE prep. Shah sir read my SOP line-by-line and rebuilt it. Got into Stanford!" },
  { initial: "R", name: "Rohan K.", when: "5 months ago", text: "4 US admits + $22k scholarship. Visa mock interviews are the secret sauce — approved in 7 minutes." },
  { initial: "A", name: "Ananya S.", when: "8 months ago", text: "Comprehensive TOEFL practice material. Recommend to every aspirant. Genuine, ethical guidance." },
  { initial: "K", name: "Kabir M.", when: "1 year ago", text: "Highly recommend for MS aspirants. Shortlist matched my profile exactly; doc-design polished every essay." },
];

const MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.5!2d79.0743!3d21.1383!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjHCsDA4JzE3LjkiTiA3OcKwMDQnMjcuNSJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin";
const MAPS_LINK = "https://maps.app.goo.gl/NJxBgLEBiaTMszpz7";
const REVIEWS_LINK = "https://maps.app.goo.gl/NJxBgLEBiaTMszpz7";

export default function GoogleMapsReviews() {
  return (
    <section id="reviews" className="grid-bg py-20 md:py-28 bg-[#FAFAFA]" data-testid="google-maps-reviews-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-10">
          <span className="inline-block bg-[#c92e34] text-white text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border-2 border-[#282829] rotate-2">
            Find Us · Read Us
          </span>
          <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
            Drop by, or read what <span className="text-[#282829]">they</span> say.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Map */}
          <div className="lg:col-span-7">
            <div className="bg-white border-2 border-[#282829] rounded-3xl overflow-hidden brutal-shadow-red">
              <div className="aspect-[4/3] sm:aspect-[16/10] w-full">
                <iframe
                  src={MAPS_EMBED}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Shah Overseas Edu — Nagpur"
                />
              </div>
              <div className="p-5 border-t-2 border-[#282829] flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  <MapPin className="text-[#c92e34] mt-0.5 flex-shrink-0" size={18} />
                  <div className="text-sm font-medium leading-tight">
                    <div className="font-bold">Shah Overseas Education</div>
                    <div className="text-[#282829]/60 text-xs">Plot 20, First Floor, Near Samata Layout Garden, North Ambazari Road, Nagpur</div>
                  </div>
                </div>
                <a
                  href={MAPS_LINK}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="open-in-maps-btn"
                  className="inline-flex items-center gap-2 bg-[#282829] hover:bg-[#c92e34] text-white font-bold px-4 py-2.5 rounded-full border-2 border-[#282829] text-xs transition-colors"
                >
                  Open in Maps <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div className="lg:col-span-5">
            <div className="bg-white border-2 border-[#282829] rounded-3xl p-5 brutal-shadow-ink">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-1 text-[#c92e34]">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <div className="text-xs font-bold tracking-widest uppercase text-[#282829]/60 mt-1">
                    Google Reviews · 4.9 · 500+ ratings
                  </div>
                </div>
              </div>
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                {REVIEWS.map((r, i) => (
                  <motion.div
                    key={r.name}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="bg-[#FAFAFA] border-2 border-[#282829] rounded-2xl p-4"
                    data-testid={`google-review-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#c92e34] text-white border-2 border-[#282829] flex items-center justify-center font-anton flex-shrink-0">
                        {r.initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2">
                          <span className="font-display text-sm">{r.name}</span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-[#282829]/50">· {r.when}</span>
                        </div>
                        <div className="flex items-center gap-0.5 mt-0.5 text-[#c92e34]">
                          {Array.from({ length: 5 }).map((_, k) => (
                            <Star key={k} size={10} fill="currentColor" />
                          ))}
                        </div>
                      </div>
                      <Quote className="text-[#c92e34] flex-shrink-0" size={14} />
                    </div>
                    <p className="mt-2 text-sm text-[#282829]/80 leading-relaxed">{r.text}</p>
                  </motion.div>
                ))}
              </div>
              <a
                href={REVIEWS_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="read-all-reviews-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#282829] hover:bg-[#c92e34] text-white font-bold px-4 py-3 rounded-full border-2 border-[#282829] text-xs transition-colors"
              >
                Read all reviews on Google <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
